/*
 * Knative Revisions Card Component
 * Glassmorphism + Monospace Style
 */

import React from 'react';
import { Box, Typography, Card, CardHeader, CardContent, Chip, LinearProgress } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { useKubernetesEnhancedStyles, colors, monoFont } from '../styles/theme';
import { KnativeRevisionStatus } from '../types';

interface KnativeRevisionsCardProps {
  revisions: KnativeRevisionStatus[];
  loading?: boolean;
}

interface RevisionItemProps {
  revision: KnativeRevisionStatus;
}

const RevisionItem: React.FC<RevisionItemProps> = ({ revision }) => {
  const classes = useKubernetesEnhancedStyles();
  
  const isReady = revision.conditions?.find(c => c.type === 'Ready')?.status === 'True';
  const hasTraffic = (revision.trafficPercent || 0) > 0;
  
  const statusColor = isReady ? colors.statusSuccess : colors.statusError;
  const statusGradient = isReady ? colors.gradientSuccess : colors.gradientError;
  
  return (
    <Box className={classes.deploymentItem}>
      <Box className={classes.deploymentHeader}>
        <Box
          className={`${classes.statusDot} ${isReady ? classes.statusRunning : classes.statusFailed}`}
        />
        <Typography className={classes.deploymentName}>
          {revision.name}
        </Typography>
        {revision.isLatest && (
          <Chip
            icon={<StarIcon style={{ fontSize: '0.8rem', color: '#fbbf24' }} />}
            label="latest"
            size="small"
            style={{
              background: 'rgba(251, 191, 36, 0.15)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              color: '#fbbf24',
              fontFamily: monoFont,
              fontSize: '0.65rem',
              height: 20,
            }}
          />
        )}
        {hasTraffic && (
          <Chip
            label={`${revision.trafficPercent}%`}
            size="small"
            style={{
              background: 'rgba(14, 165, 233, 0.15)',
              border: '1px solid rgba(14, 165, 233, 0.3)',
              color: colors.statusInfo,
              fontFamily: monoFont,
              fontSize: '0.7rem',
              fontWeight: 600,
              height: 20,
            }}
          />
        )}
      </Box>
      
      {/* Traffic bar (if has traffic) */}
      {hasTraffic && (
        <Box className={classes.progressBar} style={{ marginBottom: 12 }}>
          <Box
            className={classes.progressBarFill}
            style={{
              width: `${revision.trafficPercent}%`,
              background: colors.gradientCyan,
              boxShadow: `0 0 10px ${colors.statusInfo}`,
            }}
          />
        </Box>
      )}
      
      <Box className={classes.deploymentInfo}>
        <Box className={classes.deploymentInfoItem}>
          <Typography className={classes.deploymentInfoLabel}>generation</Typography>
          <Typography className={classes.deploymentInfoValue}>
            #{revision.generation}
          </Typography>
        </Box>
        <Box className={classes.deploymentInfoItem}>
          <Typography className={classes.deploymentInfoLabel}>replicas</Typography>
          <Typography className={classes.deploymentInfoValue}>
            {revision.replicas}
          </Typography>
        </Box>
        <Box className={classes.deploymentInfoItem}>
          <Typography className={classes.deploymentInfoLabel}>image</Typography>
          <Typography 
            className={classes.deploymentInfoValue}
            style={{ 
              maxWidth: 180, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={revision.image}
          >
            {revision.image.split('/').pop()?.split('@')[0] || revision.image}
          </Typography>
        </Box>
        <Box className={classes.deploymentInfoItem}>
          <Typography className={classes.deploymentInfoLabel}>age</Typography>
          <Typography className={classes.deploymentInfoValue}>
            {revision.age}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const KnativeRevisionsCard: React.FC<KnativeRevisionsCardProps> = ({
  revisions,
  loading,
}) => {
  const classes = useKubernetesEnhancedStyles();
  
  return (
    <Card className={classes.card}>
      <CardHeader
        title="// REVISIONS"
        className={classes.cardHeader}
        subheader={
          <Typography style={{ 
            fontSize: '0.7rem', 
            color: colors.textMuted,
            fontFamily: monoFont,
            letterSpacing: '0.5px',
          }}>
            {revisions.length} revision{revisions.length !== 1 ? 's' : ''}
          </Typography>
        }
      />
      <CardContent className={classes.cardContent}>
        {loading ? (
          <Box className={classes.loading}>
            <Typography>loading...</Typography>
          </Box>
        ) : revisions.length === 0 ? (
          <Box className={classes.empty}>
            <Typography>no revisions found</Typography>
          </Box>
        ) : (
          revisions.map((revision) => (
            <RevisionItem key={revision.name} revision={revision} />
          ))
        )}
      </CardContent>
    </Card>
  );
};

